import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  GraduationCap,
  UtensilsCrossed,
  UserPlus,
  ShieldCheck,
  Star,
  Heart,
  Gift,
  Truck,
  Fish,
  Sparkles
} from 'lucide-react';
import { accusativeUnit } from '../utils';

// Character images — fallback when character_image_url is null
import fishProfessor from 'url:../assets/characters/fish-professor.webp';
import crabEconomist from 'url:../assets/characters/crab-economist.webp';
import friendsTogether from 'url:../assets/characters/friends-together.webp';
import octopusChef from 'url:../assets/characters/octopus-chef.webp';
import octopusFisherman from 'url:../assets/characters/octopus-fisherman.webp';
import crabDetective from 'url:../assets/characters/crab-detective.webp';

// Icon name → component mapping (for API-driven stories)
const ICON_MAP = {
  GraduationCap, UtensilsCrossed, UserPlus, ShieldCheck,
  Star, Heart, Gift, Truck, Fish, Sparkles
};

// Fallback character images (for stories with asset paths or null)
const CHARACTER_FALLBACK = {
  'fish-professor': fishProfessor,
  'crab-economist': crabEconomist,
  'octopus-chef': octopusFisherman,
  'crab-detective': crabDetective,
  'friends-together': friendsTogether,
  'octopus-fisherman': octopusFisherman,
};

/**
 * StoriesHighlights - Instagram-style story circles for tutorials and promotions
 *
 * Supports two modes:
 * 1. API-driven: stories prop from /api/stories (dynamic content from admin panel)
 * 2. Fallback: hardcoded FALLBACK_STORIES when API unavailable
 */

const FALLBACK_STORIES = [
  {
    id: 'how-to-order',
    title: 'Как\nзаказать',
    icon: GraduationCap,
    gradient: 'from-cyan-500 to-blue-500',
    color: '#0EA5E9',
    characterImage: fishProfessor,
    characterName: 'Профессор Нимо',
    slides: [
      {
        title: 'Выбирайте в каталоге',
        description: 'Добавляйте товары в корзину — как в обычном магазине',
        benefits: ['Свежие морепродукты напрямую от поставщиков', 'Цены ниже магазинных на 30-40%', 'Каталог обновляется каждый сбор']
      },
      {
        title: 'Оплата за точный вес',
        description: 'Сумма замораживается на карте, списывается только после взвешивания',
        benefits: ['Платите за реальный вес', 'Отмена бесплатно', 'Безопасно через банк']
      },
      {
        title: 'Доставим свежим',
        description: 'Забираем у поставщика и доставляем в термобоксах',
        benefits: ['Температура -18°C всю дорогу', 'Доставка за 1 день после закупки', 'Уведомим в Telegram']
      }
    ]
  },
  {
    id: 'our-catch',
    title: 'Наш\nулов',
    icon: UtensilsCrossed,
    gradient: 'from-orange-500 to-red-500',
    color: '#F97316',
    characterImage: octopusFisherman,
    characterName: 'Осьминог Рыбак',
    slides: [] // Заполняется динамически из products в buildCatchSlides()
  },
  {
    id: 'invite-friends',
    title: 'Зовите\nдрузей',
    icon: UserPlus,
    gradient: 'from-green-500 to-emerald-500',
    color: '#10B981',
    characterImage: friendsTogether,
    characterName: 'Заказываем вместе',
    slides: [
      {
        title: 'Пригласите друга',
        description: 'Получите скидку на следующий заказ',
        benefits: ['Другу тоже скидка на первый заказ', 'Скидки не сгорают', 'Тратьте на любые товары']
      },
      {
        title: 'Чем больше нас',
        description: 'Тем ниже цены от поставщика для всех',
        benefits: ['Оптовые объёмы = оптовые цены', 'Доставка дешевле для каждого', 'Шире ассортимент']
      },
      {
        title: 'Расскажите друзьям',
        description: 'Выгодно вам — выгодно всем',
        benefits: ['Поделитесь ссылкой в чате', 'Друзья получат скидку', 'Вы тоже получите скидку']
      }
    ]
  },
  {
    id: 'guarantee',
    title: 'Гарантия',
    icon: ShieldCheck,
    gradient: 'from-violet-500 to-purple-500',
    color: '#8B5CF6',
    characterImage: crabDetective,
    characterName: 'Краб Детектив',
    slides: [
      {
        title: 'Проверенные поставщики',
        description: 'Работаем только с надёжными партнёрами',
        benefits: ['Сертификаты качества', 'Постоянный контроль', 'Только свежая продукция']
      },
      {
        title: 'Термобоксы -18°C',
        description: 'От поставщика до вашей двери — холодовая цепь не нарушена',
        benefits: ['Профессиональные термобоксы', 'Контроль температуры', 'Свежесть гарантирована']
      },
      {
        title: 'Не устроило — вернём',
        description: 'Если качество не соответствует — полный возврат',
        benefits: ['Быстрый возврат денег', 'Без лишних вопросов', 'Напишите в Telegram']
      }
    ]
  }
];

// Генерируем слайды "Наш улов" из реальных товаров
const buildCatchSlides = (products) => {
  if (!products || products.length === 0) {
    return [
      { title: 'Скоро здесь появятся товары', description: 'Следите за обновлениями каталога' }
    ];
  }
  // Берём до 3 товаров с фото (приоритет), иначе любые
  const withImages = products.filter(p => p.image_url);
  const source = withImages.length >= 3 ? withImages : products;
  const selected = source.slice(0, 3);

  return selected.map(p => ({
    title: p.product_name,
    description: p.description || '',
    image: p.image_url || null,
    price: p.price ? `${parseFloat(p.price).toLocaleString('ru-RU')} ₽/${accusativeUnit(p.unit || 'кг')}` : null
  }));
};

/**
 * Resolve character image URL: API URL → direct use, asset path → fallback map, null → null
 */
const resolveCharacterImage = (url) => {
  if (!url) return null;
  // Full URL (S3 or http) — use directly
  if (url.startsWith('http')) return url;
  // Asset path like /assets/characters/fish-professor.webp — extract key
  for (const [key, img] of Object.entries(CHARACTER_FALLBACK)) {
    if (url.includes(key)) return img;
  }
  return null;
};

/**
 * Convert API story format to internal component format
 */
const apiStoryToInternal = (apiStory) => ({
  id: apiStory.slug,
  title: apiStory.title,
  icon: ICON_MAP[apiStory.icon_name] || Star,
  gradient: apiStory.gradient,
  color: apiStory.color,
  characterImage: resolveCharacterImage(apiStory.character_image_url),
  characterName: apiStory.character_name,
  characterScale: apiStory.character_scale || 1,
  characterRotation: apiStory.character_rotation || 0,
  slideCharacterScale: apiStory.slide_character_scale || 1,
  slideCharacterRotation: apiStory.slide_character_rotation || 0,
  badge: apiStory.badge,
  slides: (apiStory.slides || []).map(s => ({
    title: s.title,
    description: s.description || '',
    benefits: s.benefits || null,
    image: s.image_url || null,
    price: s.price_label || null,
    elements: s.elements || null,
  })),
});

const StoriesHighlights = ({ onStoryClick, stories: apiStories = [], products = [] }) => {
  const [currentStoryIndex, setCurrentStoryIndex] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Use API stories if available, otherwise fallback to hardcoded
  const stories = React.useMemo(() => {
    let result;
    if (apiStories && apiStories.length > 0) {
      result = apiStories.map(apiStoryToInternal);
    } else {
      // Fallback: use hardcoded stories with product injection
      result = FALLBACK_STORIES.map(story => {
        if (story.id === 'our-catch') {
          return { ...story, slides: buildCatchSlides(products) };
        }
        return story;
      });
    }
    // Персонажи теперь управляются через админку — IMAGE_OVERRIDES убраны
    return result;
  }, [apiStories, products]);

  const handleStoryClick = (story, index) => {
    setCurrentStoryIndex(index);
    setCurrentSlide(0);
    if (onStoryClick) {
      onStoryClick(story);
    }
  };

  const closeStory = () => {
    setCurrentStoryIndex(null);
    setCurrentSlide(0);
  };

  // Go to next story circle (Instagram-style)
  const goToNextStory = () => {
    if (currentStoryIndex < stories.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
      setCurrentSlide(0);
    } else {
      // Last story - close modal
      closeStory();
    }
  };

  // Go to previous story circle
  const goToPrevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
      setCurrentSlide(0);
    }
  };

  return (
    <>
      {/* Stories Highlights Container */}
      <div className="stories-highlights mb-6">
        <div className="px-4 md:px-6 lg:px-10">
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 md:max-w-[1200px] md:mx-auto">
            Узнайте больше
          </h2>
        </div>

        {/* Mobile: горизонтальный скролл */}
        <div className="overflow-x-auto scrollbar-hide px-3 pt-2 md:hidden">
          <div className="flex gap-3 pb-2">
            {stories.map((story, index) => (
              <StoryCircle
                key={story.id}
                story={story}
                onClick={() => handleStoryClick(story, index)}
              />
            ))}
          </div>
        </div>

        {/* Desktop: кружки по центру с подписями */}
        <div className="hidden md:flex justify-center gap-8 lg:gap-12 px-6 lg:px-10 pt-2">
          {stories.map((story, index) => (
            <StoryCircle
              key={story.id}
              story={story}
              desktop
              onClick={() => handleStoryClick(story, index)}
            />
          ))}
        </div>
      </div>

      {/* Fullscreen Story Modal - Instagram style */}
      {currentStoryIndex !== null && (
        <StoryModal
          story={stories[currentStoryIndex]}
          allStories={stories}
          currentStoryIndex={currentStoryIndex}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          onClose={closeStory}
          onNextStory={goToNextStory}
          onPrevStory={goToPrevStory}
        />
      )}
    </>
  );
};

/**
 * StoryCircle - Individual story highlight circle
 * @param {boolean} desktop - Увеличенный вариант для desktop с однострочной подписью
 */
const StoryCircle = ({ story, onClick, desktop = false }) => {
  // Desktop: кружок 80px, подпись в одну строку
  // Mobile: кружок 64px (75px контейнер), подпись многострочная
  const circleSize = desktop ? 'w-20 h-20' : 'w-16 h-16';
  const imgSize = desktop ? 'w-16 h-16' : 'w-14 h-14';
  const iconSize = desktop ? 'w-10 h-10' : 'w-8 h-8';
  const containerWidth = desktop ? '100px' : '75px';
  const reducedMotion = prefersReducedMotion();
  // При reduced-motion не анимируем hover-масштаб/прозрачность кольца
  const ringMotionClass = reducedMotion ? '' : 'group-hover:opacity-70 transition-opacity';
  const innerMotionClass = reducedMotion ? '' : 'transition-transform duration-200 group-hover:scale-105';

  return (
    <div
      className="story-circle flex-shrink-0 flex flex-col items-center cursor-pointer group"
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`История: ${(story.title || '').replace(/\n/g, ' ')}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e);
        }
      }}
      style={{ width: containerWidth }}
    >
      {/* Circle with Gradient Border */}
      <div className="relative mb-2">
        {/* Gradient Ring */}
        <div
          className={`absolute inset-0 rounded-full bg-gradient-to-br ${story.gradient} opacity-80 ${ringMotionClass} blur-[2px]`}
          style={{ padding: '2.5px' }}
        />

        {/* Inner Circle — трёхслойный glassmorphism как в CycleStatusWidget */}
        <div
          className={`relative ${circleSize} rounded-full overflow-hidden ${innerMotionClass}`}
          style={{
            background: `linear-gradient(135deg, ${story.color}18, ${story.color}08)`,
            boxShadow: `0 4px 14px ${story.color}28, inset 0 1px 0 rgba(255,255,255,0.3)`
          }}
        >
          {/* Слой 1: Персонаж/иконка (ПОД стеклом) */}
          <div className="absolute inset-0 flex items-center justify-center">
            {story.characterImage ? (
              <img
                src={story.characterImage}
                alt={story.characterName}
                className={`${imgSize} object-contain`}
                style={{ transform: `scale(${story.characterScale || 1}) rotate(${story.characterRotation || 0}deg)` }}
              />
            ) : (
              <story.icon
                className={iconSize}
                style={{ color: story.color }}
                strokeWidth={2}
              />
            )}
          </div>

          {/* Слой 2: Стеклянный overlay */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.12) 100%)',
              boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.3), inset 0 -1px 1px rgba(0,0,0,0.05)'
            }}
          />

          {/* Badge (e.g., "NEW", "ВАЖНО") */}
          {story.badge && (
            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-md z-10">
              {story.badge}
            </div>
          )}
        </div>
      </div>

      {/* Title / Label */}
      <div className="text-center">
        {desktop ? (
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 leading-tight truncate max-w-[100px]">
            {story.title.replace(/\n/g, ' ')}
          </p>
        ) : (
          <p className="text-[11px] font-medium text-gray-700 dark:text-gray-200 leading-tight whitespace-pre-line max-w-[75px]">
            {story.title}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * StoryModal - Instagram-style fullscreen story viewer
 *
 * Mechanics (like Instagram):
 * - Fullscreen without navigation
 * - Tap zones: left 30% = back, right 70% = forward
 * - Long press = pause (no indicator)
 * - Swipe down = close
 * - Swipe left = next story circle
 * - Auto-advance with progress bar animation
 * - After last slide → go to next story circle
 */
// Уважаем системную настройку «уменьшить движение»
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Helper: get transform style from elements array for a specific key
const getElementStyle = (elements, key) => {
  if (!elements || !Array.isArray(elements)) return {};
  const el = elements.find(e => e.key === key && e.type === 'position');
  if (!el || (el.x === 0 && el.y === 0 && !el.rotation && (!el.scale || el.scale === 1))) return {};
  return {
    transform: `translate(${el.x || 0}px, ${el.y || 0}px) rotate(${el.rotation || 0}deg) scale(${el.scale || 1})`,
  };
};

const StoryModal = ({ story, allStories, currentStoryIndex, currentSlide, setCurrentSlide, onClose, onNextStory, onPrevStory }) => {
  const slides = (story && Array.isArray(story.slides)) ? story.slides : [];
  const slide = slides[currentSlide];
  const reducedMotion = prefersReducedMotion();
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchHandledRef = useRef(false); // Prevent double-fire on touch devices
  const SLIDE_DURATION = 6000; // 6 seconds per slide

  // Go to next slide, or next story circle if last slide
  // MUST be defined before useEffect that uses it (TDZ - Temporal Dead Zone)
  const goNext = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      // Last slide - go to next story circle (Instagram-style)
      onNextStory();
    }
  }, [currentSlide, slides.length, setCurrentSlide, onNextStory]);

  // Go to previous slide, or previous story circle if first slide
  const goPrev = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else if (currentStoryIndex > 0) {
      // First slide - go to previous story circle
      onPrevStory();
    }
  }, [currentSlide, setCurrentSlide, currentStoryIndex, onPrevStory]);

  // Auto-advance timer (CSS handles the animation)
  useEffect(() => {
    if (isPaused) return;

    const timer = setTimeout(() => {
      goNext();
    }, SLIDE_DURATION);

    return () => clearTimeout(timer);
  }, [currentSlide, isPaused, currentStoryIndex, goNext]);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Закрытие модалки по Escape (доступность с клавиатуры)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Handle tap zones (Instagram style: left 30% = back, right 70% = forward)
  // Only for mouse clicks - touch is handled by touchEnd
  const handleTap = (e) => {
    // Skip if this click was triggered by touch (already handled)
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const tapZone = x / rect.width;

    if (tapZone < 0.3) {
      goPrev();
    } else {
      goNext();
    }
  };

  // Touch handlers for swipe and long press
  const handleTouchStart = (e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
    setIsPaused(true);
  };

  const handleTouchEnd = (e) => {
    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };

    const deltaX = touchEnd.x - touchStartRef.current.x;
    const deltaY = touchEnd.y - touchStartRef.current.y;

    // Swipe down to close (threshold 80px)
    if (deltaY > 80 && Math.abs(deltaX) < 50) {
      onClose();
      return;
    }

    // Horizontal swipe between story circles (threshold 80px - more than tap threshold)
    if (Math.abs(deltaX) > 80) {
      if (deltaX > 0) {
        // Swipe right - go to previous story circle
        if (currentStoryIndex > 0) {
          onPrevStory();
        } else {
          goPrev();
        }
      } else {
        // Swipe left - go to next story circle
        onNextStory();
      }
    } else if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
      // Small movement - treat as tap
      const rect = containerRef.current.getBoundingClientRect();
      const x = touchEnd.x - rect.left;
      const tapZone = x / rect.width;

      if (tapZone < 0.3) {
        goPrev();
      } else {
        goNext();
      }
    } else {
      // It was a tap, not a swipe
      const rect = containerRef.current.getBoundingClientRect();
      const x = touchEnd.x - rect.left;
      const tapZone = x / rect.width;

      if (tapZone < 0.3) {
        goPrev();
      } else {
        goNext();
      }
    }

    setIsPaused(false);
    // Mark that touch was handled to prevent duplicate click event
    touchHandledRef.current = true;
  };

  const handleTouchMove = (e) => {
    // Prevent scrolling while in story
    e.preventDefault();
  };

  // Guard: нет истории/слайдов или слайд вне диапазона — не рендерим модалку (без краша/белого экрана)
  if (!story || slides.length === 0 || !slide) {
    return null;
  }

  return (
    /* Backdrop — чёрный фон на весь экран, клик вне панели закрывает */
    <div
      className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Story panel — mobile: fullscreen, desktop: телефонный формат 9:16 по центру */}
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={story.characterName ? `История: ${story.characterName}` : 'История'}
        className="relative w-full h-full md:w-auto md:h-[min(92vh,800px)] md:aspect-[9/16] md:rounded-2xl md:overflow-hidden flex flex-col bg-black"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
        onClick={handleTap}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        {/* Progress Bars - Instagram style */}
        <div className="absolute top-2 left-2 right-2 flex gap-1 z-20" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          {slides.map((_, index) => (
            <div
              key={index}
              className="flex-1 h-[3px] bg-white/30 rounded-full overflow-hidden"
            >
              {index < currentSlide ? (
                <div className="h-full w-full bg-white rounded-full" />
              ) : index === currentSlide ? (
                <div
                  key={`progress-${currentStoryIndex}-${currentSlide}`}
                  className="h-full bg-white rounded-full"
                  style={reducedMotion ? {
                    // reduced-motion: без анимации, полоса заполнена (авто-переход по таймеру сохраняется)
                    width: '100%'
                  } : {
                    animation: isPaused ? 'none' : `progressFill ${SLIDE_DURATION}ms linear forwards`,
                    animationPlayState: isPaused ? 'paused' : 'running'
                  }}
                />
              ) : (
                <div className="h-full w-0 bg-white rounded-full" />
              )}
            </div>
          ))}
        </div>

        {/* CSS Animation for progress bar */}
        <style>{`
          @keyframes progressFill {
            from { width: 0%; }
            to { width: 100%; }
          }
        `}</style>

        {/* Header with character info and close button */}
        <div className="absolute top-6 left-3 right-3 flex items-center justify-between z-20" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <div className="flex items-center gap-2">
            {story.characterImage && (
              <div className="w-8 h-8 rounded-full bg-white/20 overflow-hidden border border-white/30">
                <img
                  src={story.characterImage}
                  alt={story.characterName}
                  className="w-full h-full object-contain"
                  style={{ transform: `scale(${story.slideCharacterScale || 1}) rotate(${story.slideCharacterRotation || 0}deg)` }}
                />
              </div>
            )}
            <span className="text-white text-sm font-medium">{story.characterName}</span>
          </div>

          <button
            className="w-9 h-9 flex items-center justify-center rounded-full text-white/90 hover:text-white transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.25), inset 0 -1px 1px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.2)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
            aria-label="Закрыть историю"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-20 relative">
          {/* Background image */}
          {slide.image && (
            <div className="absolute inset-0 z-0">
              <img
                src={slide.image}
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40" />
            </div>
          )}

          {/* Character (if no background image) */}
          {!slide.image && story.characterImage && (
            <div className="mb-6 relative z-10">
              <img
                src={story.characterImage}
                alt={story.characterName}
                className="w-28 h-28 object-contain drop-shadow-2xl"
                style={{ transform: `scale(${story.slideCharacterScale || 1}) rotate(${story.slideCharacterRotation || 0}deg)` }}
              />
            </div>
          )}

          {/* Title */}
          <h2 className="text-2xl font-bold text-white text-center mb-3 relative z-10" style={getElementStyle(slide.elements, 'title')}>
            {slide.title}
          </h2>

          {/* Цена (для товарных слайдов) */}
          {slide.price && (
            <div className="mb-3 px-4 py-2 rounded-xl bg-white/15 backdrop-blur-sm relative z-10">
              <span className="text-lg font-bold text-white">{slide.price}</span>
            </div>
          )}

          {/* Description */}
          {slide.description && (
            <p className="text-white/80 text-center mb-6 leading-relaxed max-w-sm relative z-10" style={getElementStyle(slide.elements, 'description')}>
              {slide.description}
            </p>
          )}

          {/* Benefits List */}
          {slide.benefits && (
            <div className="w-full max-w-sm space-y-2 relative z-10">
              {slide.benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3"
                >
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <p className="text-white/90 text-sm">
                    {benefit}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Sticker elements from admin */}
          {slide.elements && Array.isArray(slide.elements) && slide.elements
            .filter(el => el.type === 'sticker' && el.src && !el.src.startsWith('blob:'))
            .map((el, i) => (
              <div
                key={el.key || i}
                className="pointer-events-none relative z-10"
                style={{
                  transform: `translate(${el.x || 0}px, ${el.y || 0}px) rotate(${el.rotation || 0}deg) scale(${el.scale || 1})`,
                }}
              >
                <img src={el.src} alt="" className="w-16 h-16 object-contain drop-shadow-2xl" />
              </div>
            ))
          }
        </div>

        {/* Bottom hint */}
        <div className="absolute bottom-6 left-0 right-0 text-center" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <p className="text-white/40 text-xs">
            {isPaused ? 'Отпустите для продолжения' : 'Нажмите для продолжения'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StoriesHighlights;
